import { useEffect, useState } from 'react';
import './styles.css';

export type CollectionImage = {
  src: string;
  original: string;
  width: number;
  height: number;
};

type PhotoCollectionProps = {
  images: CollectionImage[];
  label: string;
};

export const PhotoCollection = ({ images, label }: PhotoCollectionProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const close = () => setActiveIndex(null);
  const previous = () => {
    if (activeIndex === null) return;
    setActiveIndex((activeIndex + images.length - 1) % images.length);
  };
  const next = () => {
    if (activeIndex === null) return;
    setActiveIndex((activeIndex + 1) % images.length);
  };

  useEffect(() => {
    if (activeIndex === null) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveIndex(null);
      }
      if (event.key === 'ArrowLeft') {
        setActiveIndex((current) => current === null ? 0 : (current + images.length - 1) % images.length);
      }
      if (event.key === 'ArrowRight') {
        setActiveIndex((current) => current === null ? 0 : (current + 1) % images.length);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, images.length]);

  return (
    <>
      <div className="photo-collection" aria-label={`${label} photo gallery`}>
        {images.map((image, index) => (
          <button
            className="photo-collection-item"
            type="button"
            key={`${image.src}-${index}`}
            onClick={() => setActiveIndex(index)}
            aria-label={`Open ${label} image ${index + 1}`}
          >
            <img
              src={image.src}
              alt={`${label} by Rue's Events Designs — ${index + 1}`}
              loading="lazy"
            />
            <span className="photo-collection-hover">View</span>
          </button>
        ))}
      </div>

      {activeIndex !== null && (
        <div className="photo-lightbox" role="dialog" aria-modal="true" aria-label={`${label} image viewer`}>
          <button className="photo-lightbox-backdrop" type="button" onClick={close} aria-label="Close image viewer" />
          <button className="photo-lightbox-close" type="button" onClick={close} aria-label="Close">×</button>
          <button className="photo-lightbox-nav photo-lightbox-nav--prev" type="button" onClick={previous} aria-label="Previous image">‹</button>
          <div className="photo-lightbox-image-wrap">
            <img src={images[activeIndex].original} alt={`${label} by Rue's Events Designs`} />
            <span>{activeIndex + 1} / {images.length}</span>
          </div>
          <button className="photo-lightbox-nav photo-lightbox-nav--next" type="button" onClick={next} aria-label="Next image">›</button>
        </div>
      )}
    </>
  );
};
